import React, { useEffect, useState } from 'react'

import VehicleDetailsGrid from './VehicleValidContainer/Modes/Components/VehicleDetailsGrid'
import Disclaimer from './VehicleValidContainer/Modes/Components/Disclaimer'

import { getVehicleWithRelated, getVehicles, getVehiclesByVin, updateVehicle } from '../state/actions'
import { Button, Divider, Grid, Header, Icon, Modal, Segment } from 'semantic-ui-react'
import { GETVehiclesResponse, IVehicle } from '@cbgms/api'
import { useTranslation } from 'react-i18next'
import { compose } from '@cbgms/base/utils/compose'
import { connect } from 'react-redux'
import { useNotifier } from '@cbgms/base/notifier'

// ---------- STATE ----------

interface IDispatchProps {
  getVehicleWithRelated: typeof getVehicleWithRelated
  getVehiclesByVin: typeof getVehiclesByVin
  updateVehicle: typeof updateVehicle
  getVehicles: typeof getVehicles
}

const enhance = compose<VinAlreadyExistsModalProps, IVinAlreadyExistsModalProps>(
  connect(null, { getVehiclesByVin, getVehicleWithRelated, updateVehicle, getVehicles })
)

// ---------- COMPONENT ----------

interface IVinAlreadyExistsModalProps {
  onSubmit?: () => void
  onCancel?: () => void
  previousVehicle?: IVehicle
  vehicle: IVehicle
  open: boolean
  vin: string
}

interface VinAlreadyExistsModalProps extends IVinAlreadyExistsModalProps, IDispatchProps {}

export const VinAlreadyExistsModal: React.FC<VinAlreadyExistsModalProps> = props => {
  const { vin, vehicle, getVehiclesByVin, open, onCancel, onSubmit, previousVehicle } = props
  const [oldVehicleForVin, setOldVehicleForVin] = useState<IVehicle>()
  const [modalOpen, setModalOpen] = useState(false)
  const { t } = useTranslation()
  const notifier = useNotifier()

  useEffect(() => {
    if (!vin || vin.length !== 17) return
    const loadAsync = async () => {
      if (previousVehicle) {
        setOldVehicleForVin(previousVehicle)
        return
      }
      try {
        const vehiclesByVinResult: GETVehiclesResponse = (await getVehiclesByVin(vin)) as any
        if (vehiclesByVinResult.Data.length > 0) {
          setOldVehicleForVin(vehiclesByVinResult.Data[0].Vehicle)
        }
      } catch {
        notifier.error(t('vehiclevalidation.error_retrieving_vehicle_with_vin'))
      }
    }
    if (open) loadAsync()
    setModalOpen(open)
  }, [open])

  const handleCancel = () => {
    if (onCancel) onCancel()
    setModalOpen(false)
  }

  const handleSubmit = async () => {
    if (onSubmit) {
      await onSubmit()
      setModalOpen(false)
    }
  }

  if (!vin || vin.length !== 17) return null

  return (
    <Modal data-testid='vin-already-exists-modal' size='large' open={modalOpen}>
      <Header icon='car' content={t('vehiclevalidation.vin_exists_modal_header')} />
      <Modal.Content>
        <Header>{t('vehiclevalidation.vin_exists_modal_description', { vin })}</Header>
        <Segment basic>
          <Grid columns={2}>
            <Grid.Column>{oldVehicleForVin && <VehicleDetailsGrid vehicle={oldVehicleForVin} className='mr3' />}</Grid.Column>
            <Grid.Column verticalAlign='middle' textAlign='center'>
              <div className='ml6 d-inline-block text-left'>
                <VehicleDetailsGrid vehicle={vehicle} className='mr3' />
              </div>
            </Grid.Column>
          </Grid>
          <Divider vertical>{t('vehiclevalidation.vin_exists_modal_divider_text')}</Divider>
        </Segment>
      </Modal.Content>
      <Modal.Actions>
        <Disclaimer />
        <Button data-testid='vinalreadyexistsmodal-btn-cancel' color='red' size='mini' onClick={handleCancel}>
          <Icon name='remove' />
          {t('global.cancel')}
        </Button>
        <Button data-testid='vinalreadyexistsmodal-btn-submit' color='green' size='small' onClick={handleSubmit}>
          <Icon name='checkmark' />
          {t('global.confirm')}
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export default enhance(VinAlreadyExistsModal)
